import dayjs from 'dayjs';
import { ICloneable } from '../../../contracts/cloneable.js';
import { IDataComparable } from '../../../contracts/data-comparable.js';
import { cloneList, isSameList } from '../../dto/dto.js';
import { TimeZone } from '../../../time/timezone.js';
import { TrueTime } from '../../../time/true-time.js';

export abstract class ISeatSelectionEntry implements IDataComparable, ICloneable {
  abstract get seats(): number[];

  abstract isValidForSession(sessionId: string, now?: dayjs.Dayjs): boolean
  abstract isExpired(now?: dayjs.Dayjs): boolean

  abstract isSame(other: this): boolean
  abstract clone(): this
}

export class TemporarySeatSelectionEntry implements ISeatSelectionEntry {
  constructor(
    public readonly sessionId: string,
    public readonly seats: number[],
    public readonly creationDate: dayjs.Dayjs,
    public readonly expirationDate: dayjs.Dayjs,
  ) {}

  static create(params: { sessionId: string, seats: number[], creationDate?: dayjs.Dayjs, expirationDate?: dayjs.Dayjs }): TemporarySeatSelectionEntry {
    const creationDate = params.creationDate ?? TrueTime.now();
    const expirationDate = params.expirationDate ?? creationDate.add(15, 'minutes');

    return new TemporarySeatSelectionEntry(params.sessionId, params.seats, creationDate, expirationDate);
  }

  isValidForSession(sessionId: string, now?: dayjs.Dayjs): boolean {
    now ??= TrueTime.now();
    return sessionId !== this.sessionId && now.isBefore(this.expirationDate);
  }

  isExpired(now?: dayjs.Dayjs): boolean {
    now ??= TrueTime.now();
    return this.expirationDate.isBefore(now);
  }

  clone(): this {
    return new TemporarySeatSelectionEntry(
        this.sessionId,
        cloneList(this.seats),
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDate(this.expirationDate),
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return other.sessionId === this.sessionId &&
        isSameList(other.seats, this.seats) &&
        other.creationDate === this.creationDate &&
        other.expirationDate === this.expirationDate;
  }

  static verifyType(dm: ISeatSelectionEntry): boolean {
    return dm instanceof TemporarySeatSelectionEntry;
  }

  static assertType(dm: ISeatSelectionEntry): dm is TemporarySeatSelectionEntry {
    return dm instanceof TemporarySeatSelectionEntry;
  }
}

export class PermanentSeatSelectionEntry implements ISeatSelectionEntry {
  constructor(
    public readonly sessionId: string,
    public readonly seats: number[],
    public readonly creationDate: dayjs.Dayjs,
  ) {}

  static create(params: { sessionId: string, seats: number[], creationDate?: dayjs.Dayjs }): PermanentSeatSelectionEntry {
    const creationDate = params.creationDate ?? dayjs();

    return new PermanentSeatSelectionEntry(params.sessionId, params.seats, creationDate);
  }

  isValidForSession(sessionId: string, now?: dayjs.Dayjs): boolean {
    return sessionId !== this.sessionId;
  }

  isExpired(now?: dayjs.Dayjs): boolean {
    return false;
  }

  clone(): this {
    return new PermanentSeatSelectionEntry(
        this.sessionId,
        cloneList(this.seats),
        TimeZone.cloneDate(this.creationDate),
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return other.sessionId === this.sessionId &&
        isSameList(other.seats, this.seats) &&
        other.creationDate === this.creationDate;
  }

  static verifyType(dm: ISeatSelectionEntry): boolean {
    return dm instanceof PermanentSeatSelectionEntry;
  }

  static assertType(dm: ISeatSelectionEntry): dm is PermanentSeatSelectionEntry {
    return dm instanceof PermanentSeatSelectionEntry;
  }
}

export class ReservedSeatSelectionEntry implements ISeatSelectionEntry {
  constructor(
    public readonly sessionId: string,
    public readonly seats: number[],
    public readonly creationDate: dayjs.Dayjs,
    public readonly expirationDate: dayjs.Dayjs | null,
    public readonly reservationId: string,
  ) {}

  static create(params: { sessionId: string, seats: number[], reservationId: string, creationDate?: dayjs.Dayjs, expirationDate?: dayjs.Dayjs }): ReservedSeatSelectionEntry {
    const creationDate = params.creationDate ?? dayjs();

    return new ReservedSeatSelectionEntry(params.sessionId, params.seats, creationDate, params.expirationDate ?? null, params.reservationId);
  }

  isValidForSession(sessionId: string, now?: dayjs.Dayjs): boolean {
    return sessionId !== this.sessionId;
  }

  isExpired(now?: dayjs.Dayjs): boolean {
    now ??= TrueTime.now();
    return this.expirationDate?.isBefore(now) ?? false;
  }

  clone(): this {
    return new ReservedSeatSelectionEntry(
        this.sessionId,
        cloneList(this.seats),
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDateOrNull(this.expirationDate),
        this.reservationId,
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return other.sessionId === this.sessionId &&
        isSameList(other.seats, this.seats) &&
        other.creationDate === this.creationDate &&
        other.expirationDate === this.expirationDate &&
        other.reservationId === this.reservationId;
  }

  static verifyType(dm: ISeatSelectionEntry): boolean {
    return dm instanceof ReservedSeatSelectionEntry;
  }

  static assertType(dm: ISeatSelectionEntry): dm is ReservedSeatSelectionEntry {
    return dm instanceof ReservedSeatSelectionEntry;
  }
}

export class PassSeatSelectionEntry implements ISeatSelectionEntry {
  constructor(
    public readonly seats: number[],
    public readonly locks: number[],
    public readonly templateId: string,
    public readonly creationDate: dayjs.Dayjs,
  ) {}

  static create(
      params: {
        seats: number[],
        locks: number[],
        templateId: string,
        creationDate?: dayjs.Dayjs
      },
  ): PassSeatSelectionEntry {
    const creationDate = params.creationDate ?? dayjs();

    return new PassSeatSelectionEntry(
        params.seats,
        params.locks,
        params.templateId,
        creationDate,
    );
  }

  isExpired(now?: dayjs.Dayjs | undefined): boolean {
    return false;
  }

  isValidForSession(sessionId: string, now?: dayjs.Dayjs | undefined): boolean {
    return true;
  }

  clone(): this {
    return new PassSeatSelectionEntry(
        cloneList(this.seats),
        cloneList(this.locks),
        this.templateId,
        TimeZone.cloneDate(this.creationDate),
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return isSameList(other.seats, this.seats) &&
        isSameList(other.locks, this.locks) &&
        other.templateId === this.templateId &&
        other.creationDate === this.creationDate;
  }

  static verifyType(dm: ISeatSelectionEntry): boolean {
    return dm instanceof PassSeatSelectionEntry;
  }

  static assertType(dm: ISeatSelectionEntry): dm is PassSeatSelectionEntry {
    return dm instanceof PassSeatSelectionEntry;
  }
}

export class ArtificialSeatSelectionEntry implements ISeatSelectionEntry {
  private constructor(
    public readonly seats: number[],
    public readonly creationDate: dayjs.Dayjs,
  ) {}

  static create(args: {
    seats: number[],
    creationDate: dayjs.Dayjs
  }): ArtificialSeatSelectionEntry {
    return new ArtificialSeatSelectionEntry(
        args.seats,
        args.creationDate,
    );
  }

  isValidForSession(sessionId: string, now?: dayjs.Dayjs): boolean {
    return true;
  }

  isExpired(now?: dayjs.Dayjs): boolean {
    return false;
  }

  clone(): this {
    return new ArtificialSeatSelectionEntry(
        cloneList(this.seats),
        TimeZone.cloneDate(this.creationDate),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(other.seats, this.seats) &&
        other.creationDate === this.creationDate;
  }

  static verifyType(dm: ISeatSelectionEntry): boolean {
    return dm instanceof ArtificialSeatSelectionEntry;
  }

  static assertType(dm: ISeatSelectionEntry): dm is ArtificialSeatSelectionEntry {
    return dm instanceof ArtificialSeatSelectionEntry;
  }
}
